import { useEffect, useRef, useState } from "react";

export interface GlassSelectOption {
  value: any;
  label: string;
}

interface GlassSelectProps {
  options: GlassSelectOption[];
  value: any;
  onChange: (value: any) => void;
  // Accessible name for the trigger button (e.g. the group's label).
  ariaLabel?: string;
  className?: string;
}

/**
 * GlassSelect — the shared single-select glass dropdown (frosted pill trigger +
 * popover menu + outside-click close). It is the canonical version of the
 * learning-path's filter dropdown: a glass pill button that reveals a frosted
 * listbox, used wherever a single-choice filter should read as a control rather
 * than a tab strip (e.g. the reports "Ders" / "Zaman aralığı" filters).
 * Prefix: qgs-. Styles live in glassSelectStyles (aggregated in libraryStyles).
 */
const GlassSelect = ({
  options,
  value,
  onChange,
  ariaLabel,
  className = "",
}: GlassSelectProps) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", onDoc);
    return () => window.removeEventListener("mousedown", onDoc);
  }, [open]);
  const cur = options.find((o) => o.value === value) || options[0];
  return (
    <div className={"qgs" + (className ? " " + className : "")} ref={ref}>
      <button
        type="button"
        className={"qgs-btn" + (open ? " is-open" : "")}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => setOpen((v) => !v)}
      >
        <span>{cur ? cur.label : ""}</span>
        <svg
          className="qgs-chev"
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {open ? (
        <div className="qgs-menu" role="listbox">
          {options.map((o) => (
            <button
              key={String(o.value)}
              type="button"
              role="option"
              aria-selected={o.value === value}
              className={"qgs-opt" + (o.value === value ? " is-active" : "")}
              onClick={() => {
                onChange(o.value);
                setOpen(false);
              }}
            >
              {o.label}
              {o.value === value ? <span className="qgs-check">✓</span> : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default GlassSelect;
